import React from "react";
import { Link } from "react-router-dom";
import { trackMarketingFunnel } from "../../utils/marketingAnalytics";

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2 w-5 h-5">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const LayersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-purple-400">
    <path d="m12 2 8 4-8 4-8-4 8-4z" />
    <path d="m4 10 8 4 8-4" />
    <path d="m4 14 8 4 8-4" />
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-violet-400">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-sky-400">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const ScaleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-pink-400">
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="M7 21h10" />
    <path d="M12 3v18" />
    <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
  </svg>
);

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-purple-500 flex-shrink-0">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FEATURES = [
  {
    icon: UploadIcon,
    iconBg: "bg-violet-500/10",
    title: "Scenario input and policy configuration",
    desc: "Set up baseline and what-if scenarios across incentive levels, policy assumptions, infrastructure availability, and regional adoption conditions.",
  },
  {
    icon: GridIcon,
    iconBg: "bg-sky-500/10",
    title: "Infrastructure and grid readiness insights",
    desc: "Evaluate how charging availability, utility context, and deployment constraints may influence the feasibility and timing of EV adoption under different scenarios.",
  },
  {
    icon: LayersIcon,
    iconBg: "bg-purple-500/10",
    title: "Integrated market and community context",
    desc: "Bring together freight activity, fleet demand, community characteristics, logistics patterns, and built-environment signals for more grounded policy evaluation.",
  },
  {
    icon: ScaleIcon,
    iconBg: "bg-pink-500/10",
    title: "Policy impact and equity evaluation",
    desc: "Assess how proposed policies or incentive programs may affect adoption outcomes, public benefits, and equity priorities across stakeholder groups.",
  },
];

const STATS = [
  { value: "10+", label: "Policy and market layers", desc: "Combine data of transportation, charging, grid, community, regulatory, and more" },
  { value: "Multi-scenario", label: "What-if analysis", desc: "Compare incentive, policy, and adoption pathways side by side" },
  { value: "Clear", label: "Decision support", desc: "Turn complex policy and adoption data into visuals and metrics that are easier to understand and act on" },
];

const FAQ = [
  {
    q: "What kinds of decisions can PolicyPlan support?",
    a: "PolicyPlan is designed for policy impact analysis, incentive program design, EV adoption scenario evaluation, and regional planning decisions that require integrated transportation, infrastructure, grid, and community context.",
  },
  {
    q: "Can we compare multiple policy or incentive scenarios?",
    a: "Yes. Teams can evaluate multiple scenarios side by side, compare assumptions, and review how different policies or funding structures may affect adoption outcomes and implementation priorities.",
  },
  {
    q: "Can results be shared across agencies or external stakeholders?",
    a: "Yes. Outputs can be exported for reporting, cross-team review, and stakeholder communication, helping agencies document assumptions, metrics, and scenario comparisons clearly.",
  },
];

const USE_CASES = [
  {
    title: "Policy Impact Analysis",
    desc: "Evaluate how regulations and program rules may influence EV adoption and infrastructure needs.",
    img: "/img/solutions/regulator-policy-impact.jpeg",
    backupPrompt: "A realistic public-sector policy analysis scene with EV adoption maps, scenario charts, and regional planning dashboards on large screens",
  },
  {
    title: "Incentive Program Design",
    desc: "Compare incentive structures to identify where funding can drive the greatest adoption impact.",
    img: "/img/homepage/regulator.png",
    backupPrompt: "A realistic meeting scene of public-sector analysts reviewing EV incentive strategies with dashboards, funding scenarios, and regional comparison maps",
  },
  {
    title: "EV Adoption Scenario Planning",
    desc: "Explore EV adoption scenarios across regions and timelines using integrated data insights.",
    img: "/img/solutions/regulator-ev-adoption.jpeg",
    backupPrompt: "A realistic planning dashboard showing EV adoption scenarios across regions with charts, maps, timelines, and infrastructure overlays",
  },
];

function Regulators() {
  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-40 pb-24 overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50/40 to-fuchsia-50/30">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-200/50 via-transparent to-transparent" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-purple-200/40 rounded-full blur-3xl" />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6">
              Evaluate EV policy with
              <span className="block pb-3 bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">data-driven insights</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl">
              PolicyPlan helps regulators, public agencies, and program designers evaluate policy impacts, incentive strategies, and EV adoption scenarios with integrated transportation, infrastructure, grid, and community data.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                onClick={() => trackMarketingFunnel("regulator_detail_primary_cta_click")}
                className="inline-flex items-center justify-center gap-2 font-medium bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-purple-500/25 hover:scale-105 transition-all"
              >
                Request a Demo
                <ArrowRightIcon />
              </Link>
              <Link
                to="/#solutions"
                onClick={() => trackMarketingFunnel("regulator_detail_secondary_cta_click")}
                className="inline-flex items-center justify-center gap-2 font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-6 text-lg rounded-xl transition-colors"
              >
                Back to Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent mb-2">{s.value}</div>
                <div className="text-slate-900 font-semibold text-lg mb-1">{s.label}</div>
                <div className="text-slate-500 text-sm">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Core capabilities</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Policy-ready metrics for faster, more consistent location and program decisions.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                  <div className={`w-12 h-12 rounded-xl ${f.iconBg} flex items-center justify-center mb-4`}>
                    <Icon />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-50 py-24">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white via-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Built for Regulatory Workflows</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">From corridor screening to funding review, apply one consistent framework across locations and programs.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {USE_CASES.map((item) => (
              <div key={item.title} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img src={item.img} alt={item.backupPrompt} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <h3 className="absolute bottom-4 left-5 text-xl font-bold text-white">{item.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-900 mb-2">{item.q}</h3>
                <p className="text-sm text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-purple-600/15 via-transparent to-transparent" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Modernize Policy Evaluation?</h2>
          <p className="text-slate-400 mb-10">Standardized metrics · Transparent methods · Clear decisions</p>
          <Link
            to="/contact"
            onClick={() => trackMarketingFunnel("regulator_detail_final_cta_click")}
            className="inline-flex items-center justify-center gap-2 font-medium h-10 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-10 py-6 text-lg rounded-xl shadow-lg shadow-purple-500/25"
          >
            Contact Sales
            <ArrowRightIcon />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Regulators;